import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  EmptyState,
  ErrorState,
  Field,
  FormActions,
  FormSection,
  Input,
  PageHeader,
  ResumoValor,
  Skeleton,
} from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import ListaProdutos from '../../../components/produto/ListaProdutos';

import { fetchAssociadoPorId } from '../../../api/associado/associadoApi';
import {
  fetchSgaProdutos,
  vincularProdutosLocal,
  Produto,
  isProdutoCompulsorio,
  isProdutoPadrao,
} from '../../../api/sga-produto/sga-produtoApi';
import { atualizarVeiculo } from '../../../api/carro/carroApi';
import { calcularRateio } from '../../../api/rateio/rateioApi';
import { isCarroVivo } from '../../../utils/carroSituacao';
import { numeroParaMoeda, parseMoeda } from '../../../utils/moeda';

import { TERMO } from '../../termo/routes';
import { ADESOES } from '../../adesoes/routes';

import { RootState, store } from '../../../store/store';
import { updateValorAdesao } from '../../../store/associadoSlice';
import { salvarEtapaRascunho } from '../../../utils/adesaoFluxo';

/**
 * `/produto` — **etapa 4 dos TRÊS fluxos** (reescrita na at-60, frente C/C3).
 *
 * ⚠️ Adesão, Substituição Veicular e Substituição de Titular convergem aqui
 * (`plan-c` §1.4), e por isso o cabeçalho **não tem sobretítulo**: nomear um dos
 * três seria mentir nos outros dois. O `Stepper` continua correto para todos —
 * as seis etapas são as mesmas nos três.
 *
 * ## O que mudou de comportamento
 *
 *   1. 🐛 **O erro deixou de ser MUDO.** O estado `error` era preenchido e
 *      **nunca renderizado**: falha ao buscar os produtos deixava a tela com uma
 *      lista vazia, indistinguível de "este perfil não tem produtos" (só um toast
 *      passava, e ele desaparece). Agora é `ErrorState` com "Tentar novamente".
 *   2. **O carregamento não cobre mais a tela** — era `FullScreenLoader`, virou
 *      `Skeleton` no lugar da lista, então o cabeçalho e o valor de adesão
 *      continuam visíveis (§4.4 do `plan-c`).
 *   3. 🐛 **O valor de adesão digitado com separador de milhar era gravado 1000×
 *      menor.** O parse era `parseFloat(valor.replace(',', '.'))`: digitar
 *      "1.350,00" produzia `parseFloat("1.350.00")` = **1,35**. Passou a usar o
 *      `parseMoeda` (at-30), que é o parser canônico do projeto.
 *   4. 🐛 **O valor de adesão agora chega ao TERMO mesmo sem ser digitado.** Ele
 *      só ia ao slice no `onChange`; quem aceitava o padrão de 350 salvava 350 no
 *      banco (o `handleSalvar` sempre leu o estado local) mas gerava o contrato
 *      com o campo **em branco**. O `dispatch` passou a acontecer também no
 *      salvamento, com a mesma string.
 *   5. **A tela ganhou "Voltar"** (não tinha cabeçalho) e **Stepper** "Etapa 4 de 6".
 *   6. **Fim dos 2 segundos** de espera antes de ir para `/termo`.
 *   7. **Campo de adesão vazio passou a ser recusado com mensagem.** Ele é
 *      alcançável — o campo **se limpa ao focar** —, e antes mandava `NaN` ao
 *      backend, que voltava como um "Erro ao atualizar" genérico. ⚠️ Assumir 0
 *      seria pior: zero é exatamente o valor que o item 52 combateu chegando ao
 *      SGA. E **sem cadastro no fluxo** (`associadoId` nulo) a tela diz isso, em
 *      vez de ficar num `Skeleton` que nunca sai.
 *
 * ⚠️ **O que NÃO mudou:** a composição da mensalidade (cota + rateio + produtos,
 * invariante da at-21, que faz o número desta tela bater com o valor total do
 * termo), o `codigosCompulsorios` travado, a ordenação compulsórios → padrão →
 * demais (at-29: `padrao` **não** vem pré-marcado) e a base de faixa pelo valor
 * protegido (at-30).
 */
const ProdutoPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const voltar = useVoltar();

  const token = useSelector((state: RootState) => state.auth.accessToken);
  const associadoId = useSelector((state: RootState) => state.associado.associadoId);

  const [veiculoId, setVeiculoId] = useState<string | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([]);
  const [valorAdesao, setValorAdesao] = useState<string>('350');
  // Mensalidade (at-21): mesma composição do valor total do termo —
  // valor da cota (taxa administrativa) + rateio + produtos selecionados.
  const [valorCota, setValorCota] = useState<number>(0);
  const [valorRateio, setValorRateio] = useState<number | null>(null);
  /*
   * ⚠️ Só começa carregando quando há de fato o que buscar. Com `true` fixo,
   * abrir `/produto` sem fluxo (sem `associadoId` — link direto, ou o cadastro
   * da etapa 3 tendo falhado) deixaria o `Skeleton` **para sempre**: o efeito
   * abaixo nem chama a busca. Seria o mesmo spinner eterno que esta branch
   * consertou em `/assinatura`.
   */
  const [carregando, setCarregando] = useState(Boolean(associadoId));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroValor, setErroValor] = useState<string | null>(null);

  // Compulsórios (flag do SGA): pré-selecionados e travados.
  // Padrão: apenas ordenados no topo, SEM pré-seleção — a flag `padrao` do SGA não
  // significa "já contratado" (o SGA Consultor não a soma no boleto). Ver at-29.
  const [codigosCompulsorios, setCodigosCompulsorios] = useState<string[]>([]);

  const buscarProdutos = useCallback(async () => {
    if (!associadoId || !token) return;

    setCarregando(true);
    setErro(null);

    try {
      const associado = await fetchAssociadoPorId(associadoId);

      if (!associado || !associado.id) {
        throw new Error('Associado não encontrado.');
      }

      if (!associado.carros || associado.carros.length === 0) {
        throw new Error('Nenhum veículo encontrado para o associado.');
      }

      // at-37 (item 39): "ativo" = em circulação. Filtrar só por `deletedAt`
      // passaria a incluir o veículo SUBSTITUIDO, que não recebe produtos.
      const carrosAtivos = associado.carros.filter(isCarroVivo);

      if (carrosAtivos.length === 0) {
        throw new Error('Nenhum veículo ativo encontrado para o associado.');
      }

      const carro = carrosAtivos[0];
      setVeiculoId(carro.id);

      // at-30 (item 18): lookups por faixa usam o valor PROTEGIDO (fallback FIPE)
      const valorBaseFaixa = Number(carro.valorProtegido ?? carro.valorFipe);

      const resposta = await fetchSgaProdutos(
        associado.regional,
        carro.cooperativa,
        carro.tipoVeiculo,
        valorBaseFaixa,
      );

      const lista = resposta || [];

      const compulsorios = lista.filter(isProdutoCompulsorio);
      const padrao = lista.filter((p) => !isProdutoCompulsorio(p) && isProdutoPadrao(p));
      const demais = lista.filter((p) => !isProdutoCompulsorio(p) && !isProdutoPadrao(p));

      setProdutos([...compulsorios, ...padrao, ...demais]);
      setCodigosCompulsorios(compulsorios.map((p) => p.codigo_produto));
      setProdutosSelecionados(compulsorios.map((p) => p.codigo_produto));

      setValorCota(Number(carro.valorCota) || 0);

      // Rateio: mesma chamada que a tela de termo faz (sem codigo_cota), para o
      // valor exibido aqui bater com o valorTotal do termo gerado na sequência.
      try {
        const rateio = await calcularRateio(
          associado.regional,
          carro.tipoVeiculo,
          valorBaseFaixa,
        );
        setValorRateio(Number(rateio.valorRateio) || 0);
      } catch {
        // sem rateio não dá para mostrar a mensalidade — mas a tela continua
        // funcionando (seleção de produtos e salvamento não dependem dele)
        setValorRateio(null);
      }
    } catch (err: any) {
      setErro(err?.message || 'Erro ao buscar produtos.');
    } finally {
      setCarregando(false);
    }
  }, [associadoId, token]);

  useEffect(() => {
    if (associadoId) void buscarProdutos();
  }, [associadoId, buscarProdutos]);

  const alternarProduto = (codigo: string) =>
    setProdutosSelecionados((anterior) =>
      anterior.includes(codigo) ? anterior.filter((c) => c !== codigo) : [...anterior, codigo],
    );

  const totalProdutos = produtosSelecionados.reduce((acumulado, codigo) => {
    const produto = produtos.find((p) => p.codigo_produto === codigo);
    return acumulado + parseFloat(produto?.valor_produto || '0');
  }, 0);

  const mensalidade = valorRateio === null ? null : valorCota + valorRateio + totalProdutos;

  const salvar = async () => {
    if (!associadoId || !veiculoId) {
      toast.error('Erro: Associado ou veículo não encontrado.');
      return;
    }

    /*
     * ⚠️ O parse é o `parseMoeda` (at-30), não `parseFloat(replace(',', '.'))`:
     * aquele lia "1.350,00" como 1,35 — o separador de milhar virava decimal.
     */
    const valorConvertido = parseMoeda(valorAdesao);

    /*
     * Campo vazio é alcançável: ele **se limpa ao focar** (o "350" é padrão, não
     * placeholder). Antes isso mandava `NaN` ao backend — que vira `null` no JSON
     * e volta como erro genérico "Erro ao atualizar o valor de adesão".
     *
     * ⚠️ Assumir 0 seria pior que recusar: zero é exatamente o valor que o item 52
     * combateu chegando ao SGA. Aqui a saída é pedir o valor.
     */
    if (!Number.isFinite(valorConvertido)) {
      setErroValor('Informe o valor de adesão.');
      return;
    }
    setErroValor(null);

    setSalvando(true);

    try {
      // at-54 (item 52): mudar o valor de adesão de um veículo que já está no SGA
      // dispara o reenvio do cadastro. Antes, o valor ficava só no banco local e o
      // SGA guardava 0 para sempre — o consultor via 350 na tela e o SGA, zero.
      const respostaVeiculo = await atualizarVeiculo(veiculoId, {
        valorAdesao: valorConvertido,
      });

      const respostaVinculo = await vincularProdutosLocal(veiculoId, produtosSelecionados);

      /*
       * O valor também vai ao slice AQUI, e não só no `onChange` do campo: quem
       * aceita o padrão sem digitar nada salvava 350 no banco e gerava o contrato
       * com o campo em branco, porque o termo lê `carro.valorAdesao` do estado.
       */
      dispatch(updateValorAdesao(valorAdesao));

      // Rascunho (at-38): produtos salvos, próxima tela é o termo. `associadoId`
      // e `carroId` vão junto para o caso de o rascunho ainda não existir (as
      // etapas anteriores podem ter falhado ao gravar).
      void salvarEtapaRascunho(dispatch, () => store.getState(), 'TERMO', {
        associadoId,
      });

      toast.success('Valor de adesão atualizado com sucesso!', { autoClose: 2000 });

      // at-36 (item 25): com o veículo já no SGA, os produtos são vinculados na hora.
      // Só avisa quando não deu — a seleção está salva e o cron de retry (2h) tenta de
      // novo. "SEM_VEICULO_NO_SGA" não é pendência de produto: o vínculo sobe junto com
      // o veículo, então não vale poluir a tela com isso.
      const situacao = respostaVinculo?.envioSga?.situacao;
      const produtosPendentes = situacao === 'SGA_INDISPONIVEL' || situacao === 'FALHA';
      // at-54 (item 52): idem para o valor de adesão. `NAO_APLICAVEL` é sucesso (o
      // veículo ainda não subiu e o cadastro pendente levará o valor), então só
      // `PENDENTE` vira aviso.
      const adesaoPendente = respostaVeiculo?.envioSga?.status === 'PENDENTE';

      // Um aviso só: dois toasts sobre a mesma indisponibilidade do SGA viram ruído.
      if (produtosPendentes || adesaoPendente) {
        const oQue =
          produtosPendentes && adesaoPendente
            ? 'Os produtos e o valor de adesão foram salvos'
            : produtosPendentes
              ? 'Os produtos foram salvos'
              : 'O valor de adesão foi salvo';

        toast.info(`${oQue}. O envio ao SGA será feito automaticamente em instantes.`, {
          autoClose: 6000,
        });
      }

      // ⚠️ Sem `setTimeout`. O toast sobrevive à navegação (container único).
      navigate(TERMO());
    } catch {
      toast.error('Erro ao atualizar o valor de adesão.');
      setSalvando(false);
    }
  };

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Produtos" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={4} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormSection titulo="Valor de Adesão" icone="identificacao">
                <Field
                  rotulo="Valor de adesão"
                  dica="Valor de entrada cobrado do associado."
                  erro={erroValor ?? undefined}
                  campo="valorAdesao"
                >
                  <Input
                    value={valorAdesao}
                    inputMode="decimal"
                    disabled={salvando}
                    /*
                     * O "350" é o padrão real (não um `placeholder`): quem não
                     * toca no campo salva 350. Por isso ele sai ao focar, em vez
                     * de obrigar o consultor a apagá-lo.
                     */
                    onFocus={() => {
                      if (valorAdesao === '350') setValorAdesao('');
                    }}
                    onChange={(evento) => {
                      const novoValor = evento.target.value.replace(/[^0-9,.]/g, '');
                      setValorAdesao(novoValor);
                      if (erroValor) setErroValor(null);
                      dispatch(updateValorAdesao(novoValor));
                    }}
                  />
                </Field>
              </FormSection>

              <FormSection titulo="Produtos" icone="adesoes">
                {!associadoId ? (
                  /* Sem cadastro no fluxo não há perfil de onde tirar produtos —
                     e dizer isso é melhor que uma lista vazia sem explicação. */
                  <EmptyState
                    titulo="Nenhum cadastro em andamento"
                    descricao="Esta etapa é aberta depois de cadastrar o associado e o veículo. Comece uma adesão ou retome uma que esteja em andamento."
                    icone="alerta"
                    acao={
                      <Botao variante="primario" onClick={() => navigate(ADESOES())}>
                        Ir para Adesões
                      </Botao>
                    }
                  />
                ) : carregando ? (
                  <Skeleton linhas={5} altura="tw-h-12" />
                ) : erro ? (
                  <ErrorState
                    mensagem="Não foi possível carregar os produtos deste perfil."
                    detalhe={erro}
                    aoTentarNovamente={() => void buscarProdutos()}
                  />
                ) : produtos.length === 0 ? (
                  <EmptyState
                    titulo="Nenhum produto disponível"
                    descricao="Não há produtos para a regional, cooperativa e faixa de valor deste veículo."
                    icone="sem-dados"
                  />
                ) : (
                  /* at-62: a lista virou componente — `/cotacao-produtos` mostra
                     a mesma coisa e tinha uma segunda implementação dela. */
                  <ListaProdutos
                    produtos={produtos}
                    selecionados={produtosSelecionados}
                    compulsorios={codigosCompulsorios}
                    desabilitado={salvando}
                    aoAlternar={alternarProduto}
                  />
                )}
              </FormSection>

              {/* Mensalidade: a composição é a mesma do valor total do termo
                  (at-21) — trocá-la faria a tela mentir sobre o documento. */}
              <ResumoValor
                className="tw-mt-4"
                titulo="Mensalidade"
                valor={numeroParaMoeda(mensalidade ?? 0)}
                detalhe={
                  <>
                    cota {numeroParaMoeda(valorCota)} + rateio{' '}
                    {numeroParaMoeda(valorRateio ?? 0)} + produtos{' '}
                    {numeroParaMoeda(totalProdutos)}
                  </>
                }
                indisponivel={
                  mensalidade === null
                    ? 'Mensalidade indisponível — não foi possível calcular o rateio.'
                    : undefined
                }
              />

              <FormActions>
                <Botao
                  variante="primario"
                  larguraTotal
                  carregando={salvando}
                  disabled={carregando || salvando || !veiculoId}
                  onClick={() => void salvar()}
                >
                  {salvando ? 'Salvando...' : 'Continuar — Termo'}
                </Botao>
              </FormActions>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default ProdutoPage;
